import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';

import { Tenant } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { MessageService } from 'src/services/message.service';
import { TenantsService } from 'src/services/tenants.service';

import { SharedTenantService } from '../shared-tenant.service';

interface TaxonomyDimension {
  id: string;
  tenantId: string;
  projectId: string | null;
  name: string;
  role: string | null;
  strategy: string;
  llmThreshold: number;
  centroidThreshold: number;
  aggregationThreshold: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ListDimensionsResponse {
  dimensions: TaxonomyDimension[];
  meta: {
    total: number;
    active: number;
  };
}

@Component({
  selector: 'app-taxonomy-dimensions-list',
  templateUrl: './taxonomy-dimensions-list.component.html',
  styleUrls: ['./taxonomy-dimensions-list.component.scss'],
})
export class TaxonomyDimensionsListComponent implements OnInit {
  dimensions: TaxonomyDimension[] = [];
  isLoadingDimensions = false;
  error: string | null = null;
  tenant: Partial<Tenant> | null = null;

  // Create dimension modal
  showCreateDimensionModal = false;
  newDimensionForm = {
    name: '',
    role: '',
    strategy: '',
    llmThreshold: 0.5,
    centroidThreshold: 0.0,
    aggregationThreshold: 0.35,
  };

  // Edit dimension modal
  showEditDimensionModal = false;
  editingDimension: TaxonomyDimension | null = null;
  editDimensionForm = {
    name: '',
    role: '',
    strategy: '',
    llmThreshold: 0.5,
    centroidThreshold: 0.0,
    aggregationThreshold: 0.35,
    isActive: true,
  };

  private strategyDefaults = {
    large: { llm: 0.5, centroid: 0.0, aggregation: 0.35 },
    'small-llm': { llm: 0.85, centroid: 0.2, aggregation: 0.5 }, // Granular: Very high LLM threshold for maximum precision
    'small-centroid': { llm: 0.3, centroid: 0.9, aggregation: 0.2 }, // Broad: Low LLM, high centroid for broader categories
    'small-hybrid': { llm: 0.7, centroid: 0.1, aggregation: 0.6 }, // Prompt-driven granularity with reasonable thresholds
  };

  private roleDefaults = {
    primary: { strategy: 'large', llm: 0.5, centroid: 0.0, aggregation: 0.35 },
    broad: { strategy: 'small-centroid', llm: 0.3, centroid: 0.9, aggregation: 0.2 }, // Optimized for broader categories
    granular: { strategy: 'small-hybrid', llm: 0.7, centroid: 0.1, aggregation: 0.6 }, // Prompt-driven granularity with reasonable thresholds
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private messageService: MessageService,
    private sharedTenantService: SharedTenantService,
    private tenantsService: TenantsService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const tenantId = params.get('tenantId');

      if (tenantId) {
        // Try to get tenant from shared service first
        const sharedTenant = this.sharedTenantService.getSelectedTenant();
        if (sharedTenant && sharedTenant.id === tenantId) {
          this.tenant = sharedTenant;
          this.loadDimensions();
        } else {
          // Fetch tenant data directly from API
          this.loadTenantAndData(tenantId);
        }
      } else {
        this.error = 'Missing tenant ID in URL.';
      }
    });
  }

  loadTenantAndData(tenantId: string) {
    this.isLoadingDimensions = true;
    this.error = null;

    // Fetch full tenant data including subdomain
    this.tenantsService
      .getTenantById(tenantId)
      .then((tenant) => {
        this.tenant = tenant;
        this.sharedTenantService.setSelectedTenant(tenant);
        this.loadDimensions();
      })
      .catch((error) => {
        this.isLoadingDimensions = false;
        this.error = 'Failed to load tenant: ' + (error.message || 'Unknown error');
      });
  }

  loadDimensions() {
    if (!this.tenant?.id) return;

    this.isLoadingDimensions = true;
    this.error = null;

    this.apiService
      .get<ListDimensionsResponse>(`/knowledge-base/taxonomy/${this.tenant.id}/taxonomy-dimensions`)
      .subscribe({
        next: (response) => {
          this.dimensions = response.dimensions;
          this.isLoadingDimensions = false;
        },
        error: (err) => {
          this.isLoadingDimensions = false;
          this.error = 'Failed to load dimensions: ' + (err.message || 'Unknown error');
          this.messageService.showError('Failed to load dimensions: ' + (err.message || 'Unknown error'));
        },
      });
  }

  selectDimension(dimension: TaxonomyDimension) {
    this.router.navigate(['/super-admin/taxonomy', this.tenant?.id, dimension.id]);
  }

  async openCreateDimensionModal() {
    this.showCreateDimensionModal = true;
    this.newDimensionForm = {
      name: '',
      role: '',
      strategy: '',
      llmThreshold: 0.5,
      centroidThreshold: 0.0,
      aggregationThreshold: 0.35,
    };
  }

  async createDimension() {
    if (!this.tenant?.id || !this.newDimensionForm.name || !this.newDimensionForm.strategy) {
      this.messageService.showError('Please fill in all required fields');
      return;
    }

    if (
      this.newDimensionForm.llmThreshold < 0 ||
      this.newDimensionForm.llmThreshold > 1 ||
      this.newDimensionForm.centroidThreshold < 0 ||
      this.newDimensionForm.centroidThreshold > 1 ||
      this.newDimensionForm.aggregationThreshold < 0 ||
      this.newDimensionForm.aggregationThreshold > 1
    ) {
      this.messageService.showError('All threshold values must be between 0 and 1');
      return;
    }

    const loading = await this.loadingController.create({
      message: 'Creating dimension...',
    });
    await loading.present();

    try {
      const payload = {
        name: this.newDimensionForm.name,
        role: this.newDimensionForm.role,
        strategy: this.newDimensionForm.strategy,
        llmThreshold: Number(this.newDimensionForm.llmThreshold),
        centroidThreshold: Number(this.newDimensionForm.centroidThreshold),
        aggregationThreshold: Number(this.newDimensionForm.aggregationThreshold),
      };

      await this.apiService.post(`/knowledge-base/taxonomy/${this.tenant.id}/taxonomy-dimensions`, payload).toPromise();

      this.messageService.show('Dimension created successfully');
      this.showCreateDimensionModal = false;
      this.loadDimensions();
    } catch (error: any) {
      this.messageService.showError('Failed to create dimension: ' + (error.message || 'Unknown error'));
    } finally {
      loading.dismiss();
    }
  }

  cancelCreateDimension() {
    this.showCreateDimensionModal = false;
  }

  async openEditDimensionModal(dimension: TaxonomyDimension) {
    this.editingDimension = dimension;
    this.editDimensionForm = {
      name: dimension.name,
      role: dimension.role || '',
      strategy: dimension.strategy,
      llmThreshold: dimension.llmThreshold,
      centroidThreshold: dimension.centroidThreshold,
      aggregationThreshold: dimension.aggregationThreshold,
      isActive: dimension.isActive,
    };
    this.showEditDimensionModal = true;
  }

  async updateDimension() {
    if (
      !this.tenant?.id ||
      !this.editingDimension ||
      !this.editDimensionForm.name ||
      !this.editDimensionForm.strategy
    ) {
      this.messageService.showError('Please fill in all required fields');
      return;
    }

    if (
      this.editDimensionForm.llmThreshold < 0 ||
      this.editDimensionForm.llmThreshold > 1 ||
      this.editDimensionForm.centroidThreshold < 0 ||
      this.editDimensionForm.centroidThreshold > 1 ||
      this.editDimensionForm.aggregationThreshold < 0 ||
      this.editDimensionForm.aggregationThreshold > 1
    ) {
      this.messageService.showError('All threshold values must be between 0 and 1');
      return;
    }

    const loading = await this.loadingController.create({
      message: 'Updating dimension...',
    });
    await loading.present();

    try {
      const payload = {
        name: this.editDimensionForm.name,
        role: this.editDimensionForm.role || null,
        strategy: this.editDimensionForm.strategy,
        llmThreshold: Number(this.editDimensionForm.llmThreshold),
        centroidThreshold: Number(this.editDimensionForm.centroidThreshold),
        aggregationThreshold: Number(this.editDimensionForm.aggregationThreshold),
        isActive: this.editDimensionForm.isActive,
      };

      await this.apiService
        .put(`/knowledge-base/taxonomy/${this.tenant.id}/taxonomy-dimensions/${this.editingDimension.id}`, payload)
        .toPromise();

      this.messageService.show('Dimension updated successfully');
      this.showEditDimensionModal = false;
      this.editingDimension = null;
      this.loadDimensions();
    } catch (error: any) {
      this.messageService.showError('Failed to update dimension: ' + (error.message || 'Unknown error'));
    } finally {
      loading.dismiss();
    }
  }

  cancelEditDimension() {
    this.showEditDimensionModal = false;
    this.editingDimension = null;
  }

  onEditStrategyChange(strategy: string) {
    const defaults = this.strategyDefaults[strategy as keyof typeof this.strategyDefaults];
    if (defaults) {
      this.editDimensionForm.llmThreshold = defaults.llm;
      this.editDimensionForm.centroidThreshold = defaults.centroid;
      this.editDimensionForm.aggregationThreshold = defaults.aggregation;
    }
  }

  onEditRoleChange(role: string) {
    const defaults = this.roleDefaults[role as keyof typeof this.roleDefaults];
    if (defaults) {
      this.editDimensionForm.strategy = defaults.strategy;
      this.editDimensionForm.llmThreshold = defaults.llm;
      this.editDimensionForm.centroidThreshold = defaults.centroid;
      this.editDimensionForm.aggregationThreshold = defaults.aggregation;
    }
  }

  onStrategyChange(strategy: string) {
    const defaults = this.strategyDefaults[strategy as keyof typeof this.strategyDefaults];
    if (defaults) {
      this.newDimensionForm.llmThreshold = defaults.llm;
      this.newDimensionForm.centroidThreshold = defaults.centroid;
      this.newDimensionForm.aggregationThreshold = defaults.aggregation;
    }
  }

  onRoleChange(role: string) {
    const defaults = this.roleDefaults[role as keyof typeof this.roleDefaults];
    if (defaults) {
      this.newDimensionForm.strategy = defaults.strategy;
      this.newDimensionForm.llmThreshold = defaults.llm;
      this.newDimensionForm.centroidThreshold = defaults.centroid;
      this.newDimensionForm.aggregationThreshold = defaults.aggregation;
    }
  }

  goBack() {
    this.router.navigate(['/super-admin/taxonomy']);
  }

  getRoleColor(role: string | null): string {
    switch (role) {
      case 'primary':
        return 'primary';
      case 'broad':
        return 'secondary';
      case 'granular':
        return 'tertiary';
      default:
        return 'medium';
    }
  }

  getStrategyColor(strategy: string): string {
    switch (strategy.toLowerCase()) {
      case 'large':
        return 'success';
      case 'small-llm':
        return 'warning';
      case 'small-centroid':
        return 'secondary';
      case 'small-hybrid':
        return 'tertiary';
      default:
        return 'medium';
    }
  }
}
